// /src/app/tools/backlink-checker/page.tsx
'use client';

import { useState } from 'react';
import { Search, Download, BarChart2, TrendingUp, GitCompare } from 'lucide-react';
import Papa from 'papaparse';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { backlinkCheckerContent } from '../../data/backlink-checker-content';

type BacklinkResult = {
  url: string;
  anchorText: string;
  isDofollow: boolean;
  pageTitle: string;
  pageAuthority: number;
  domainAuthority: number;
  lastCrawled: string;
};

type BacklinkMetrics = {
  pageAuthority: number;
  domainAuthority: number;
  totalBacklinks: number;
};

export default function BacklinkChecker() {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<BacklinkResult[]>([]);
  const [metrics, setMetrics] = useState<BacklinkMetrics | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [competitorUrl, setCompetitorUrl] = useState('');
  const [competitorData, setCompetitorData] = useState<{
    metrics: BacklinkMetrics | null;
    results: BacklinkResult[];
  } | null>(null);
  const [activeTab, setActiveTab] = useState('backlinks');

  const checkBacklinks = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) {
      setError('Please enter a valid URL');
      return;
    }

    try {
      new URL(url);
      if (competitorUrl) new URL(competitorUrl);
    } catch (e) {
      setError('Please enter valid URLs including http:// or https://');
      return;
    }

    setIsLoading(true);
    setError('');
    setShowResults(false);
    setResults([]);
    setMetrics(null);
    setCompetitorData(null);

    try {
      const params = new URLSearchParams({
        url,
        ...(competitorUrl && { compareWith: competitorUrl })
      });

      const response = await fetch(`/api/backlinks?${params}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch backlinks');
      }

      const data = await response.json();

      setResults(data.current?.results || []);
      setMetrics(data.current?.metrics || null);
      setCompetitorData(competitorUrl ? data.competitor : null);
      setShowResults(true);
    } catch (err) {
      console.error('Error checking backlinks:', err);
      setError(err instanceof Error ? err.message : 'Failed to check backlinks. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!results.length) return;

    const csv = Papa.unparse({
      fields: ['URL', 'Anchor Text', 'Dofollow', 'Page Authority', 'Domain Authority', 'Last Crawled'],
      data: results.map(link => [
        link.url,
        link.anchorText,
        link.isDofollow ? 'Yes' : 'No',
        link.pageAuthority,
        link.domainAuthority,
        link.lastCrawled ? new Date(link.lastCrawled).toLocaleDateString() : 'N/A'
      ])
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `backlinks-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <ToolPageIntro title="Backlink Checker">
        Check backlinks pointing to a URL. Enter any webpage to see referring domains and backlink count. Useful for SEO and link-building audits. No sign-up required.
      </ToolPageIntro>

      <form onSubmit={checkBacklinks} className="mb-8">
        <div className="space-y-4">
          <div>
            <label htmlFor="url" className="block text-sm font-medium mb-1">
              Enter URL to check
            </label>
            <input
              type="url"
              id="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full p-2 border rounded"
              required
            />
          </div>

          <div>
            <label htmlFor="competitor" className="block text-sm font-medium mb-1">
              Compare with (optional)
            </label>
            <input
              type="url"
              id="competitor"
              value={competitorUrl}
              onChange={(e) => setCompetitorUrl(e.target.value)}
              placeholder="https://competitor.com"
              className="w-full p-2 border rounded"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            <Search className="w-4 h-4 mr-2" />
            {isLoading ? 'Checking...' : 'Check Backlinks'}
          </button>

          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>
      </form>

      {showResults && (
        <div className="space-y-8">
          {/* Metrics Summary */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Backlink Metrics</h2>

            {competitorData && metrics ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border p-4 rounded">
                  <h3 className="font-medium mb-2">Your URL</h3>
                  <div className="space-y-2">
                    <p>Domain Authority: {metrics.domainAuthority.toFixed(1)}</p>
                    <p>Page Authority: {metrics.pageAuthority.toFixed(1)}</p>
                    <p>Total Backlinks: {results.length}</p>
                  </div>
                </div>
                <div className="border p-4 rounded">
                  <h3 className="font-medium mb-2">Competitor URL</h3>
                  <div className="space-y-2">
                    <p>Domain Authority: {competitorData.metrics?.domainAuthority?.toFixed(1) || 'N/A'}</p>
                    <p>Page Authority: {competitorData.metrics?.pageAuthority?.toFixed(1) || 'N/A'}</p>
                    <p>Total Backlinks: {competitorData.results?.length || 0}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <p>Domain Authority: {metrics?.domainAuthority.toFixed(1)}</p>
                <p>Page Authority: {metrics?.pageAuthority.toFixed(1)}</p>
                <p>Total Backlinks: {results.length}</p>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('backlinks')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'backlinks'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Backlinks
              </button>
              {competitorData && (
                <button
                  onClick={() => setActiveTab('comparison')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'comparison'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  Comparison
                </button>
              )}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="mt-4">
            {activeTab === 'backlinks' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Backlinks</h2>
                  <button
                    onClick={exportToCSV}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                    disabled={!results.length}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Export to CSV
                  </button>
                </div>

                {results.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            URL
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Anchor Text
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Dofollow
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Page Authority
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {results.map((link, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate" title={link.url}>
                              {link.url}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate" title={link.anchorText}>
                              {link.anchorText || 'No anchor text'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {link.isDofollow ? 'Yes' : 'No'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {link.pageAuthority.toFixed(1)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500">No backlinks found for this URL.</p>
                )}
              </div>
            )}

            {activeTab === 'comparison' && competitorData && (
              <div>
                <h2 className="text-xl font-semibold mb-4">Backlink Comparison</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium mb-2">Your Backlinks</h3>
                    {results.length > 0 ? (
                      <div className="space-y-2">
                        {results.slice(0, 10).map((link, index) => (
                          <div key={index} className="p-2 border rounded hover:bg-gray-50">
                            <p className="text-sm font-medium truncate" title={link.url}>{link.url}</p>
                            <p className="text-xs text-gray-500 truncate" title={link.anchorText}>
                              {link.anchorText || 'No anchor text'}
                            </p>
                          </div>
                        ))}
                        {results.length > 10 && (
                          <p className="text-sm text-gray-500">+ {results.length - 10} more backlinks</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500">No backlinks found for your URL.</p>
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium mb-2">Competitor's Backlinks</h3>
                    {competitorData.results.length > 0 ? (
                      <div className="space-y-2">
                        {competitorData.results.slice(0, 10).map((link, index) => (
                          <div key={index} className="p-2 border rounded hover:bg-gray-50">
                            <p className="text-sm font-medium truncate" title={link.url}>{link.url}</p>
                            <p className="text-xs text-gray-500 truncate" title={link.anchorText}>
                              {link.anchorText || 'No anchor text'}
                            </p>
                          </div>
                        ))}
                        {competitorData.results.length > 10 && (
                          <p className="text-sm text-gray-500">+ {competitorData.results.length - 10} more backlinks</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500">No backlinks found for competitor's URL.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SEO CONTENT */}
      <div className="mt-16">
        <InformationalContent
          title={backlinkCheckerContent.title}
          description={backlinkCheckerContent.description}
          content={backlinkCheckerContent.content}
          faqs={backlinkCheckerContent.faqs}
          relatedTools={backlinkCheckerContent.relatedTools}
        />
      </div>
    </div>
  );
}